/**
 * Point-in-polygon (ray casting) sobre as geometrias pequenas de `map-geo.json`.
 *
 * Só é usado para responder a um clique **fora** de Portugal continental: qual a
 * banda de ocultação mais alta que contém o ponto, e sobre que país é que ele cai.
 * As bandas têm 17-40 vértices cada e os países ~1800 no total — um ray cast é
 * microsegundos, e evita tornar 9 camadas interactivas só para descobrir isto.
 *
 * O hit-testing que **importa** — o concelho exacto sob o cursor — não passa aqui:
 * é o próprio Leaflet que o faz, com a camada de 278 polígonos. Ver EclipseLeafletMap.
 */

import type { Feature, FeatureCollection, LineString, MultiPolygon } from 'geojson';

/** `ring` em [lon, lat]. Testa a fronteira como "dentro" só por acidente de
 *  arredondamento — para o uso aqui (ler uma banda) isso é irrelevante. */
function pointInRing(lon: number, lat: number, ring: number[][]): boolean {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const xi = ring[i][0];
        const yi = ring[i][1];
        const xj = ring[j][0];
        const yj = ring[j][1];
        if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
}

/** Anel exterior conta como dentro, buracos (anéis seguintes) subtraem. */
export function pointInMultiPolygon(lon: number, lat: number, geometry: MultiPolygon): boolean {
    for (const poly of geometry.coordinates) {
        if (!poly.length || !pointInRing(lon, lat, poly[0])) continue;
        let inHole = false;
        for (let h = 1; h < poly.length; h++) {
            if (pointInRing(lon, lat, poly[h])) {
                inHole = true;
                break;
            }
        }
        if (!inHole) return true;
    }
    return false;
}

/**
 * Percorre de trás para a frente porque as bandas vêm ordenadas por threshold
 * ascendente e cada uma é "ocultação >= threshold": a mais alta que contém o ponto
 * é a resposta certa.
 */
export function highestBandAt<P extends { threshold: number }>(lon: number, lat: number, bands: FeatureCollection<MultiPolygon, P>): P | null {
    for (let i = bands.features.length - 1; i >= 0; i--) {
        const f = bands.features[i];
        if (pointInMultiPolygon(lon, lat, f.geometry)) return f.properties;
    }
    return null;
}

export function featureAt<P>(lon: number, lat: number, fc: FeatureCollection<MultiPolygon, P>): Feature<MultiPolygon, P> | null {
    for (const f of fc.features) {
        if (pointInMultiPolygon(lon, lat, f.geometry)) return f;
    }
    return null;
}

/**
 * Centro da caixa delimitadora (bbox) de um MultiPolygon — usado só para ancorar o
 * rótulo de nome de um país de contexto (Bélgica, Luxemburgo, Suíça, Marrocos,
 * Argélia, França, Andorra, Gibraltar). Não é um centróide de área a sério (não
 * pesa por polígono), mas para estas formas compactas e já recortadas à moldura do
 * mapa (`prepare()` no gerador já cortou fora qualquer território ultramarino) o
 * centro da bbox cai sempre dentro do território — não vale a pena mais precisão
 * para um rótulo de contexto.
 */
export function bboxCenterOfMultiPolygon(geometry: MultiPolygon): [number, number] {
    let minLon = Infinity;
    let maxLon = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;
    for (const poly of geometry.coordinates) {
        for (const ring of poly) {
            for (const [lon, lat] of ring) {
                if (lon < minLon) minLon = lon;
                if (lon > maxLon) maxLon = lon;
                if (lat < minLat) minLat = lat;
                if (lat > maxLat) maxLat = lat;
            }
        }
    }
    return [(minLon + maxLon) / 2, (minLat + maxLat) / 2];
}

/**
 * O vértice do polígono com a latitude mais próxima de `targetLat` — usado para
 * ancorar o rótulo de percentagem de uma banda a um ponto real da sua própria linha,
 * sem ter de calcular a intersecção exacta com um paralelo (os vértices já vêm
 * densos o suficiente — 17 a 40 por banda — para a aproximação não se notar).
 *
 * Devolvido em [lon, lat], a mesma ordem do GeoJSON.
 *
 * `targetLat` fixo (39,5°, ~meio de Portugal) dá uma leitura em linha: como a
 * ocultação sobe de oeste para leste a essa latitude, os rótulos das bandas caem em
 * sequência da esquerda (90%) para a direita (99,5%), em vez de espalhados sem
 * ordem visual pelo mapa.
 */
export function nearestPointAtLatitude(geometry: MultiPolygon, targetLat: number): [number, number] | null {
    let best: [number, number] | null = null;
    let bestDist = Infinity;
    for (const poly of geometry.coordinates) {
        for (const ring of poly) {
            for (const pt of ring) {
                const d = Math.abs(pt[1] - targetLat);
                if (d < bestDist) {
                    bestDist = d;
                    best = [pt[0], pt[1]];
                }
            }
        }
    }
    return best;
}

/**
 * O vértice do polígono com a longitude mais próxima de `targetLon` — o mesmo truque
 * que `nearestPointAtLatitude`, mas ancorando a oeste da costa em vez de a meio do
 * país. `targetLon = -9.8` (confirmado por amostragem sobre `map-geo.json`) fica a
 * oeste do litoral português em toda a sua extensão (a costa varia entre ~-8,8° no
 * Minho e ~-9,5° no Cabo da Roca) e todas as 8 bandas têm um vértice próximo — cada
 * uma alcança o Atlântico bem mais a oeste do que isto (a de 90% chega a -22,7°).
 * O resultado cai numa sequência vertical sul->norte ao largo da costa, que é o que
 * o pedido descreve.
 */
export function nearestPointAtLongitude(geometry: MultiPolygon, targetLon: number): [number, number] | null {
    let best: [number, number] | null = null;
    let bestDist = Infinity;
    for (const poly of geometry.coordinates) {
        for (const ring of poly) {
            for (const pt of ring) {
                const d = Math.abs(pt[0] - targetLon);
                if (d < bestDist) {
                    bestDist = d;
                    best = [pt[0], pt[1]];
                }
            }
        }
    }
    return best;
}

/**
 * O ponto mais próximo de `targetLon`, entre os pontos de todas as `LineString` de
 * `fc`. Usado só para o rótulo do limite de visibilidade: o gerador já entrega essa
 * linha como troços abertos, com os troços que só seguiam o bordo da grelha de
 * amostragem descartados (ver DATA.md) — aqui não há filtro nenhum a fazer, só
 * escolher o ponto mais próximo de uma longitude de referência.
 */
export function nearestPointInLinesAtLongitude(fc: FeatureCollection<LineString, unknown>, targetLon: number): [number, number] | null {
    let best: [number, number] | null = null;
    let bestDist = Infinity;
    for (const f of fc.features) {
        for (const pt of f.geometry.coordinates) {
            const d = Math.abs(pt[0] - targetLon);
            if (d < bestDist) {
                bestDist = d;
                best = [pt[0], pt[1]];
            }
        }
    }
    return best;
}
